import { useApolloClient } from '@apollo/client';
import {
  BKJButton,
  BKJCheckbox,
  BKJIcon,
  BKJLoadingSpinner,
  BKJSelectSearch,
  BKJTextInput,
} from '@bookjane2/bookjane-design-library';
import { BKJCenteredDialog } from 'components/BKJCenteredDialog';
import { Flex } from 'components/Flex';
import { H3, P } from 'components/Typography';
import { useTranslation } from 'hooks/useTranslation';
import { SKILLS_SETTINGS_PATH } from 'pages/Settings/pages/Organization/SkillsPage/SkillsPage.constants';
import {
  CenteredCell,
  EmptyStateMessage,
  SkillsTableRow,
  SkillsTableScrollArea,
  UnderlineButton,
} from 'pages/Settings/pages/Organization/SkillsPage/SkillsPage.styled';
import { LocationAllocationCell } from 'pages/Settings/pages/Organization/SkillsPage/LocationAllocationCell';
import { getSkillNameError } from 'pages/Settings/pages/Organization/SkillsPage/SkillsPage.utils';
import { useOrganizationSkillsStore } from 'pages/Settings/pages/Organization/SkillsPage/store/organizationSkills.store';
import {
  NewSkillDraft,
  OrganizationSkill,
} from 'pages/Settings/pages/Organization/SkillsPage/store/organizationSkills.store.types';
import { SettingsPageWrapper } from 'pages/Settings/SettingsPage.styled';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

export const SkillsPageEditView: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const apolloClient = useApolloClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [skillToRemove, setSkillToRemove] = useState<OrganizationSkill | null>(null);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);

  const organizationId = useOrganizationSkillsStore((state) => state.organizationId);
  const skills = useOrganizationSkillsStore((state) => state.skills);
  const newSkills = useOrganizationSkillsStore((state) => state.newSkills);
  const removedSkillIds = useOrganizationSkillsStore((state) => state.removedSkillIds);
  const renamedSkills = useOrganizationSkillsStore((state) => state.renamedSkills);
  const isLoading = useOrganizationSkillsStore((state) => state.isLoading);
  const isSaving = useOrganizationSkillsStore((state) => state.isSaving);
  const addNewSkill = useOrganizationSkillsStore((state) => state.addNewSkill);
  const renameNewSkill = useOrganizationSkillsStore((state) => state.renameNewSkill);
  const removeNewSkill = useOrganizationSkillsStore((state) => state.removeNewSkill);
  const removeSkill = useOrganizationSkillsStore((state) => state.removeSkill);
  const renameSkill = useOrganizationSkillsStore((state) => state.renameSkill);
  const applyToAllLocationsChanges = useOrganizationSkillsStore(
    (state) => state.applyToAllLocationsChanges,
  );
  const setApplyToAllLocations = useOrganizationSkillsStore(
    (state) => state.setApplyToAllLocations,
  );
  const setNewSkillApplyToAllLocations = useOrganizationSkillsStore(
    (state) => state.setNewSkillApplyToAllLocations,
  );
  const discardChanges = useOrganizationSkillsStore((state) => state.discardChanges);
  const saveSkills = useOrganizationSkillsStore((state) => state.saveSkills);

  useEffect(() => discardChanges, [discardChanges]);

  const getSkillName = useCallback(
    (skill: OrganizationSkill) => renamedSkills[skill.displayId] ?? skill.name,
    [renamedSkills],
  );

  const activeSkills = useMemo(
    () => skills.filter((skill) => !removedSkillIds.includes(skill.displayId)),
    [skills, removedSkillIds],
  );

  const visibleSkills = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    return activeSkills.filter((skill) =>
      [skill.name, getSkillName(skill)].some((name) =>
        name.toLowerCase().includes(normalizedSearchTerm),
      ),
    );
  }, [activeSkills, getSkillName, searchTerm]);

  const getOtherSkillNames = (excludedSkill?: OrganizationSkill) => [
    ...skills.filter((skill) => skill.displayId !== excludedSkill?.displayId).map(getSkillName),
    ...newSkills.map((skill) => skill.name),
  ];

  const getRenamedSkillError = (skill: OrganizationSkill) =>
    getSkillNameError(getSkillName(skill), getOtherSkillNames(skill));

  const getRenamedSkillErrorMessage = (skill: OrganizationSkill) => {
    const error = getRenamedSkillError(skill);
    return error ? t(`settings:${error}`) : undefined;
  };

  const getNewSkillError = (newSkill: NewSkillDraft) =>
    getSkillNameError(newSkill.name, [
      ...skills.map(getSkillName),
      ...newSkills.filter((skill) => skill.key !== newSkill.key).map((skill) => skill.name),
    ]);

  const getNewSkillErrorMessage = (newSkill: NewSkillDraft) => {
    const error = getNewSkillError(newSkill);
    return error ? t(`settings:${error}`) : undefined;
  };

  const newSkillNameError = getSkillNameError(newSkillName, getOtherSkillNames());
  const hasInvalidSkillName =
    activeSkills.some((skill) => !!getRenamedSkillError(skill)) ||
    newSkills.some((skill) => !!getNewSkillError(skill));
  const hasDraftChanges =
    newSkills.length > 0 ||
    removedSkillIds.length > 0 ||
    Object.keys(renamedSkills).length > 0 ||
    Object.keys(applyToAllLocationsChanges).length > 0;

  const getApplyToAllLocations = (skill: OrganizationSkill) =>
    applyToAllLocationsChanges[skill.displayId] ?? skill.applyToAllLocations;

  const closeAddForm = () => {
    setIsAddFormOpen(false);
    setNewSkillName('');
  };

  const handleAddSkill = () => {
    addNewSkill(newSkillName);
    closeAddForm();
  };

  const closeRemoveDialog = () => setSkillToRemove(null);

  const handleConfirmRemoveSkill = () => {
    if (skillToRemove) removeSkill(skillToRemove.displayId);
    closeRemoveDialog();
  };

  const handleConfirmSave = async () => {
    setIsSaveDialogOpen(false);
    if (await saveSkills(apolloClient)) history.push(SKILLS_SETTINGS_PATH);
  };

  return (
    <SettingsPageWrapper>
      <Flex flexDirection="column" width="100%" maxWidth="900px">
        <Flex minHeight="44px" margin="0 0 24px 0">
          <H3>{t('settings:SKILLS')}</H3>
        </Flex>
        <Flex flexDirection="column" gap="32px">
          <Flex maxWidth="672px">
            <P>{t('settings:SKILLS_PAGE_NOTE')}</P>
          </Flex>
          <Flex maxWidth="496px">
            <BKJSelectSearch
              name="searchTerm"
              value={searchTerm}
              placeholder={t('settings:SEARCH_SKILLS')}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
            />
          </Flex>
          {isAddFormOpen ? (
            <Flex gap="16px" alignItems="flex-end" flexWrap="wrap" padding="16px 0">
              <Flex flexDirection="column" flex="1" minWidth="240px">
                <BKJTextInput
                  name="newSkillName"
                  label={t('settings:SKILL_NAME')}
                  value={newSkillName}
                  placeholder={t('settings:SKILL_NAME_PLACEHOLDER')}
                  onChange={(event) => setNewSkillName(event.target.value)}
                  error={
                    newSkillNameError === 'SKILL_NAME_DUPLICATE'
                      ? t('settings:SKILL_NAME_DUPLICATE')
                      : undefined
                  }
                />
              </Flex>
              <Flex gap="8px" width="220px">
                <BKJButton
                  variant="GreenSolid"
                  width="106px"
                  disabled={!!newSkillNameError}
                  onClick={handleAddSkill}
                >
                  {t('settings:ADD')}
                </BKJButton>
                <BKJButton variant="PurpleOutlined" width="106px" onClick={closeAddForm}>
                  {t('auth:CANCEL')}
                </BKJButton>
              </Flex>
            </Flex>
          ) : (
            <Flex minHeight="44px" alignItems="center">
              <BKJButton
                variant="PurpleText"
                width="auto"
                disabled={isLoading}
                onClick={() => setIsAddFormOpen(true)}
              >
                <Flex alignItems="center" gap="8px">
                  <BKJIcon iconName="Add" color="PrimaryDefault" />
                  {t('settings:ADD_NEW_SKILL')}
                </Flex>
              </BKJButton>
            </Flex>
          )}
          {isLoading && (
            <Flex justifyContent="center" padding="40px 0">
              <BKJLoadingSpinner variant="Default" />
            </Flex>
          )}
          {!isLoading && (
            <SkillsTableScrollArea>
              <SkillsTableRow $isHeader $hasEditColumns>
                {t('settings:SKILL_NAME')}
                <CenteredCell>{t('location_settings:LOCATION_ALLOCATION')}</CenteredCell>
                <CenteredCell>{t('settings:APPLY_TO_ALL_LOCATIONS')}</CenteredCell>
                <CenteredCell>{t('location_settings:ACTION')}</CenteredCell>
              </SkillsTableRow>
              {newSkills.map((newSkill) => (
                <SkillsTableRow key={newSkill.key} $hasEditColumns>
                  <Flex maxWidth="320px">
                    <BKJTextInput
                      name={`new-skill-${newSkill.key}`}
                      value={newSkill.name}
                      placeholder={t('settings:SKILL_NAME_PLACEHOLDER')}
                      onChange={(event) => renameNewSkill(newSkill.key, event.target.value)}
                      error={getNewSkillErrorMessage(newSkill)}
                    />
                  </Flex>
                  <LocationAllocationCell count={0} />
                  <Flex justifyContent="center">
                    <BKJCheckbox
                      variant="GreenSolid"
                      name={`new-skill-apply-to-all-${newSkill.key}`}
                      value={newSkill.applyToAllLocations}
                      onChange={() =>
                        setNewSkillApplyToAllLocations(newSkill.key, !newSkill.applyToAllLocations)
                      }
                    />
                  </Flex>
                  <Flex justifyContent="center">
                    <UnderlineButton
                      variant="PurpleText"
                      width="auto"
                      onClick={() => removeNewSkill(newSkill.key)}
                    >
                      {t('settings:REMOVE')}
                    </UnderlineButton>
                  </Flex>
                </SkillsTableRow>
              ))}
              {visibleSkills.map((skill) => (
                <SkillsTableRow key={skill.id} $hasEditColumns>
                  <Flex maxWidth="320px">
                    <BKJTextInput
                      name={`skill-${skill.displayId}`}
                      value={getSkillName(skill)}
                      placeholder={t('settings:SKILL_NAME_PLACEHOLDER')}
                      onChange={(event) => renameSkill(skill.displayId, event.target.value)}
                      error={getRenamedSkillErrorMessage(skill)}
                    />
                  </Flex>
                  <LocationAllocationCell count={skill.communitiesSkills.length} />
                  <Flex justifyContent="center">
                    <BKJCheckbox
                      variant="GreenSolid"
                      name={`skill-apply-to-all-${skill.displayId}`}
                      value={getApplyToAllLocations(skill)}
                      onChange={() =>
                        setApplyToAllLocations(skill.displayId, !getApplyToAllLocations(skill))
                      }
                    />
                  </Flex>
                  <Flex justifyContent="center">
                    <UnderlineButton
                      variant="PurpleText"
                      width="auto"
                      onClick={() => setSkillToRemove(skill)}
                    >
                      {t('settings:REMOVE')}
                    </UnderlineButton>
                  </Flex>
                </SkillsTableRow>
              ))}
            </SkillsTableScrollArea>
          )}
          {!isLoading && !newSkills.length && !visibleSkills.length && (
            <EmptyStateMessage>
              {t(
                searchTerm.trim()
                  ? 'settings:NO_SEARCHED_SKILL_RESULTS'
                  : 'settings:NO_SKILLS_MESSAGE',
              )}
            </EmptyStateMessage>
          )}
        </Flex>
        <Flex gap="16px" flexWrap="wrap" margin="40px 0 0 0">
          <BKJButton
            variant="PurpleSolid"
            width="192px"
            minWidth="192px"
            isLoading={isSaving}
            disabled={!organizationId || !hasDraftChanges || hasInvalidSkillName || isSaving}
            onClick={() => setIsSaveDialogOpen(true)}
          >
            {t('auth:SAVE')}
          </BKJButton>
          <BKJButton
            variant="PurpleOutlined"
            width="192px"
            minWidth="192px"
            onClick={() => history.push(SKILLS_SETTINGS_PATH)}
          >
            {t('auth:CANCEL')}
          </BKJButton>
        </Flex>
      </Flex>
      <BKJCenteredDialog
        isOpen={!!skillToRemove}
        variant="Danger"
        title={t('dialog:DELETE_SKILL_CONFIRMATION')}
        text={t('dialog:DELETE_SKILL_MESSAGE')}
        cancelLabel={t('auth:CANCEL')}
        actionLabel={t('auth:DELETE')}
        onAction={handleConfirmRemoveSkill}
        onCancel={closeRemoveDialog}
        onClose={closeRemoveDialog}
      />
      <BKJCenteredDialog
        isOpen={isSaveDialogOpen}
        variant="Default"
        title={t('dialog:SAVE_SKILLS_CONFIRMATION')}
        text={t('dialog:SAVE_SKILLS_MESSAGE')}
        cancelLabel={t('auth:CANCEL')}
        actionLabel={t('auth:SAVE')}
        onAction={handleConfirmSave}
        onCancel={() => setIsSaveDialogOpen(false)}
        onClose={() => setIsSaveDialogOpen(false)}
      />
    </SettingsPageWrapper>
  );
};
